import { computed, ref, type Ref } from 'fine-jsx'

import { NODE_FIELD_FLAGS, VIDEO_PREPLAY_TIME_S } from '#constants'
import type { ClipTime, ClipTimeRational, Schema } from '#core'
import type * as pub from '#core'
import type { NonOverlappingUnion } from '#internal'
import { clamp, Rational } from 'shared/utils/math.ts'
import { rangeContainsTime } from 'shared/video/utils.ts'

import { TrackChild } from '../track-child.ts'

export interface BaseClip<T extends Schema.AnyClip> extends NonOverlappingUnion<
  TrackChild<T>,
  pub.BaseClip
> {}

export abstract class BaseClip<T extends Schema.AnyClip = Schema.AnyClip>
  extends TrackChild<T>
  implements pub.BaseClip
{
  static FIELDS = super.FIELDS.concat([
    { key: 'sourceStart', flags: NODE_FIELD_FLAGS.ReactiveProp, transform: Rational.from },
    {
      key: 'mediaRef',
      flags: NODE_FIELD_FLAGS.ReactiveProp,
      equal: (a: Schema.AssetRef | undefined, b: Schema.AssetRef | undefined) =>
        a?.id === b?.id && a?.type === b?.type,
    },
    { key: 'transition', flags: NODE_FIELD_FLAGS.ReactiveProp },

    { key: 'isReady', flags: NODE_FIELD_FLAGS.Readonly },
    { key: 'asset', flags: NODE_FIELD_FLAGS.Readonly | NODE_FIELD_FLAGS.Asset },
    { key: 'playableTime', flags: NODE_FIELD_FLAGS.Readonly },
    { key: 'presentationTime', flags: NODE_FIELD_FLAGS.Readonly },
    { key: 'expectedMediaTime', flags: NODE_FIELD_FLAGS.Readonly },
    { key: 'isInClipTime', flags: NODE_FIELD_FLAGS.Readonly },

    { key: 'gap', flags: NODE_FIELD_FLAGS.Gap },
    { key: 'link', flags: NODE_FIELD_FLAGS.Readonly },
  ] satisfies pub.NodeFieldDef<pub.BaseClip>[])

  declare readonly children: undefined

  declare private _asset: Ref<pub.MediaAsset | undefined>
  declare mediaRef: T['mediaRef']
  declare error: Ref<MediaError | undefined>

  declare private _presentationTime: Ref<ClipTime>
  declare private _playableTime: Ref<ClipTime>
  declare private _isInPresentationTime: Ref<boolean>
  declare private _isInPlayableTime: Ref<boolean>

  readonly #expectedMediaTime = computed((): number => {
    const { start, source, duration } = this.playableTime
    return clamp(this.doc.currentTime - start + source, source, source + duration)
  })
  readonly #isInClipTime = computed(() => rangeContainsTime(this.presentationTime, this.doc.currentTime))

  declare asset?: pub.MediaAsset | pub.ImageAsset | undefined
  get ['asset' as never](): pub.AnyAsset | undefined {
    return this._asset.value
  }

  get isReady(): boolean {
    return this.asset?.isLoading !== true
  }

  get presentationTime(): ClipTime {
    return this._presentationTime.value
  }
  get playableTime(): ClipTime {
    return this._playableTime.value
  }
  get expectedMediaTime(): number {
    return this.#expectedMediaTime.value
  }
  get isInClipTime(): boolean {
    return this.#isInClipTime.value
  }
  get isInPresentationTime(): boolean {
    return this._isInPresentationTime.value
  }
  get isInPlayableTime(): boolean {
    return this._isInPlayableTime.value
  }

  declare _link: Ref<Schema.NodeLink | undefined>
  get link(): Schema.NodeLink | undefined {
    return this._link.value
  }

  protected _init(): void {
    super._init()

    this._asset = computed((): pub.MediaAsset | undefined =>
      this.mediaRef?.id ? this.doc.assets.getAsset(this.mediaRef.id) : undefined,
    )
    this._presentationTime = computed(() => this._computePresentationTime())
    this._playableTime = computed(() => this._computePlayableTime())
    this._isInPresentationTime = computed(() => this._computeIsInPresentationTime())
    this._isInPlayableTime = computed(() => this._computeIsInPlayableTime())
    this._link = ref()
  }

  _computeTimeRational(): ClipTimeRational {
    const prevTime = this.prev?.timeRational
    const start = this.gap.add(prevTime ? prevTime.end : Rational.ZERO)
    const end = start.add(this.duration)

    const { duration } = this
    const source = this.sourceStart
    return { start, source, duration, end }
  }

  _computePresentationTime(): ClipTime {
    const { time, prev } = this
    const inTransition = prev?.isClip() ? prev.transition : undefined
    if (!inTransition) return time

    const inTransitionDuration = inTransition.duration.value - inTransition.duration.rate

    const start = time.start - inTransitionDuration
    const source = time.source - inTransitionDuration
    const duration = time.duration + inTransitionDuration

    return { start, source, duration, end: start + duration }
  }

  _computePlayableTime(): ClipTime {
    const presentation = this._presentationTime.value
    if (presentation.source >= 0) return presentation

    const { start, end, source, duration } = presentation
    const preplayDuration = Math.max(0, start - Math.min(VIDEO_PREPLAY_TIME_S, source))

    return {
      start: start - preplayDuration,
      source: source - preplayDuration,
      duration: duration + preplayDuration,
      end,
    }
  }

  _computeIsInPresentationTime(): boolean {
    if (this.isDisposed) return false

    const { presentationTime, doc } = this
    const docTime = doc.currentTime

    return (
      rangeContainsTime(presentationTime, docTime) ||
      // display final frame of clip at the end of the timeline
      (docTime > presentationTime.start && presentationTime.end === doc.duration)
    )
  }

  _computeIsInPlayableTime() {
    return !this.isDisposed && rangeContainsTime(this.playableTime, this.doc.currentTime)
  }

  _insertKeyframeAtTime(key: string, value: unknown, time: Rational, index?: number): void {
    super._insertKeyframeAtTime(key, value, time.clamp(Rational.ZERO, this.duration), index)
  }

  isClip(): this is pub.AnyClip {
    return true
  }
  isTrackChild(): this is pub.AnyClip {
    return true
  }

  toJSON(): Pick<T, keyof Schema.BaseClip> {
    return {
      ...super.toJSON(),
      sourceStart: this.sourceStart,
      mediaRef: this.mediaRef,
      duration: this.duration,
      transition: this.transition,
    }
  }

  _visualToJSON<T extends Extract<Schema.AnyClip, Partial<Schema.VisualProps>>>(
    this: BaseClip<T> & Schema.VisualProps,
  ): Partial<Schema.VisualProps & Pick<Schema.Base, 'effects'>> {
    const { translateX, translateY, rotate, scaleX, scaleY, opacity } = this
    const obj: Partial<Schema.VisualProps & Pick<Schema.Base, 'effects'>> = {}

    if (translateX !== 0) obj.translateX = translateX
    if (translateY !== 0) obj.translateY = translateY
    if (rotate !== 0) obj.rotate = rotate
    if (scaleX !== 1) obj.scaleX = scaleX
    if (scaleY !== 1) obj.scaleY = scaleY

    if (opacity !== 1) obj.opacity = opacity

    const effects: Schema.EffectListItem[] | undefined = this.effects.toJSON()
    if (effects) obj.effects = effects

    return obj
  }

  delete(): void {
    const { link } = this
    if (link) this.doc.deleteLink(link.id)
    super.delete()
  }
}
